import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from './ui/button';
import { cn } from '@client/lib';

interface UnsavedChangesDialogProps {
  'showWarning': boolean;
  'handleCancelClose': () => void;
  'handleConfirmClose': () => void;
  'primaryButtonText'?: string;
  'secondaryButtonText'?: string;
  'dialogTitle'?: string;
  'dialogDescription'?: string;
  'destructive'?: boolean;
  'showIcon'?: boolean;
  'data-testid'?: string;
  'cancelButtonTestId'?: string;
  'confirmButtonTestId'?: string;
}

export function UnsavedChangesDialog({
  showWarning,
  handleCancelClose,
  handleConfirmClose,
  primaryButtonText = 'Close',
  secondaryButtonText = "Don't close",
  dialogTitle = 'You have unsaved changes!',
  dialogDescription = 'If you close this dialog, you will lose all the changes you have made. Are you sure you wish to close?',
  destructive = true,
  showIcon = true,
  'data-testid': testId,
  'cancelButtonTestId': cancelButtonTestId,
  'confirmButtonTestId': confirmButtonTestId,
}: UnsavedChangesDialogProps) {
  return (
    <Dialog
      open={showWarning}
      onOpenChange={handleCancelClose}
    >
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <div className="flex items-start gap-4">
            {showIcon && (
              <div
                className={cn(
                  'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100',
                  destructive && 'bg-red-100',
                )}
              >
                <ExclamationTriangleIcon
                  className={cn(
                    'h-5 w-5 text-yellow-600',
                    destructive && 'text-red-600',
                  )}
                  aria-hidden="true"
                />
              </div>
            )}
            <div className="flex flex-col">
              <DialogTitle className="text-gray-900">{dialogTitle}</DialogTitle>
              <DialogDescription className="py-2 text-gray-700">
                {dialogDescription}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className={'flex justify-end gap-3'}>
          {secondaryButtonText !== '' && (
            <Button
              onClick={handleCancelClose}
              variant="outline"
              data-testid={cancelButtonTestId}
            >
              {secondaryButtonText}
            </Button>
          )}
          <Button
            onClick={handleConfirmClose}
            variant={destructive ? 'destructive' : 'default'}
            data-testid={confirmButtonTestId}
          >
            {primaryButtonText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
